import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import { Link } from "react-router-dom";
import { Plus, Trash, CheckCircle } from "lucide-react";
import RightArrow from "../../components/SVG";
import {
  sendEmailOtp,
  verifyEmailOtp,
  sendMobileOtp,
  verifyMobileOtp,
  registerStudent,
} from "../../services/studentServices";
import { deleteImage, uploadImages } from "../../services/uploaderServices";
import toast from "react-hot-toast";

// Success Component
const SuccessComponent = () => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "400px",
        padding: "40px",
        backgroundColor: "#f0fdf4",
        borderRadius: "12px",
        border: "2px solid #10b981",
        margin: "40px auto",
        maxWidth: "600px",
      }}
    >
      <CheckCircle
        size={80}
        style={{ color: "#10b981", marginBottom: "24px" }}
      />
      <h2
        style={{
          fontSize: "28px",
          fontWeight: 700,
          color: "#0E2A46",
          marginBottom: "16px",
          textAlign: "center",
        }}
      >
        Registration Successful!
      </h2>
      <p
        style={{
          fontSize: "16px",
          color: "#374151",
          textAlign: "center",
          lineHeight: "1.6",
          marginBottom: "12px",
        }}
      >
        Your application has been submitted successfully.
      </p>
      <p
        style={{
          fontSize: "16px",
          color: "#374151",
          textAlign: "center",
          lineHeight: "1.6",
          marginBottom: "24px",
          fontWeight: 600,
        }}
      >
        After admin approval, you will receive an email shortly.
      </p>
      <Link
        to="/"
        style={{
          padding: "12px 32px",
          backgroundColor: "#0E2A46",
          color: "#fff",
          borderRadius: "8px",
          textDecoration: "none",
          fontWeight: 600,
          transition: "background-color 0.3s",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.backgroundColor = "#1e3a5f")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.backgroundColor = "#0E2A46")
        }
      >
        Go to Home
      </Link>
    </div>
  );
};

const StudentRegisterMain = () => {
  const [institutions, setInstitutions] = useState([
    {
      id: crypto.randomUUID(),
      name: "",
      location: "",
      degree: "",
      fieldOfStudy: "",
      yearStart: "",
      targetedYearCompletion: "",
    },
  ]);

  const [fullName, setFullName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [address, setAddress] = useState("");
  const [photo, setPhoto] = useState(null);
  const [bonafideCertificate, setBonafideCertificate] = useState(null);
  const [coursesCompleted, setCoursesCompleted] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);

  const [photoUrl, setPhotoUrl] = useState(null);
  const [bonafideUrl, setBonafideUrl] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingBonafide, setUploadingBonafide] = useState(false);

  const [email, setEmail] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  const [mobile, setMobile] = useState("");
  const [mobileOtp, setMobileOtp] = useState("");
  const [mobileOtpSent, setMobileOtpSent] = useState(false);
  const [isMobileVerified, setIsMobileVerified] = useState(false);

  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Individual field errors
  const [errors, setErrors] = useState({
    fullName: "",
    dateOfBirth: "",
    email: "",
    mobile: "",
    address: "",
    photo: "",
    bonafide: "",
    coursesCompleted: "",
    agreeTerms: "",
    institutions: {},
  });

  // Validate individual field
  const validateField = (fieldName, value) => {
    let error = "";

    switch (fieldName) {
      case "fullName":
        if (!value || !value.trim()) {
          error = "Full Name is required";
        }
        break;
      case "dateOfBirth":
        if (!value) {
          error = "Date of Birth is required";
        }
        break;
      case "email":
        if (!value || !value.trim()) {
          error = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = "Please enter a valid email address";
        }
        break;
      case "mobile":
        if (!value || !value.trim()) {
          error = "Mobile number is required";
        } else if (value.length !== 10) {
          error = "Mobile number must be exactly 10 digits";
        }
        break;
      case "address":
        if (!value || !value.trim()) {
          error = "Address is required";
        }
        break;
      case "photo":
        if (!value) {
          error = "Photo is required";
        }
        break;
      case "bonafide":
        if (!value) {
          error = "Bonafide certificate is required";
        }
        break;
      case "coursesCompleted":
        if (!value) {
          error = "Please select an option";
        }
        break;
      case "agreeTerms":
        if (!value) {
          error = "You must agree to the terms and conditions";
        }
        break;
    }

    setErrors((prev) => ({ ...prev, [fieldName]: error }));
    return error === "";
  };

  const validateInstitutionField = (instId, fieldName, value) => {
    let error = "";

    if (!value || !value.trim()) {
      error = `${
        fieldName.charAt(0).toUpperCase() +
        fieldName.slice(1).replace(/([A-Z])/g, " $1")
      } is required`;
    }

    setErrors((prev) => ({
      ...prev,
      institutions: {
        ...prev.institutions,
        [instId]: {
          ...prev.institutions[instId],
          [fieldName]: error,
        },
      },
    }));

    return error === "";
  };

  const handleMobileChange = (value) => {
    const cleanedValue = value.replace(/\D/g, "");
    if (cleanedValue.length <= 10) {
      setMobile(cleanedValue);
      setIsMobileVerified(false);
      // Always validate on change
      validateField("mobile", cleanedValue);
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;

    setUploadingPhoto(true);

    try {
      const formData = new FormData();
      formData.append("images", file);

      const response = await uploadImages(formData);

      if (response.success && response.images?.[0]?.imageUrl) {
        setPhotoUrl(response.images[0].imageUrl);
        setPhoto(file);
        setErrors((prev) => ({ ...prev, photo: "" }));
        toast.success("Photo uploaded successfully");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to upload photo";
      setErrors((prev) => ({ ...prev, photo: errorMessage }));
      setPhoto(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleBonafideUpload = async (file) => {
    if (!file) return;

    setUploadingBonafide(true);

    try {
      const formData = new FormData();
      formData.append("images", file);

      const response = await uploadImages(formData);

      if (response.success && response.images?.[0]?.imageUrl) {
        setBonafideUrl(response.images[0].imageUrl);
        setBonafideCertificate(file);
        setErrors((prev) => ({ ...prev, bonafide: "" }));
        toast.success("Bonafide certificate uploaded successfully");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to upload bonafide certificate";
      setErrors((prev) => ({ ...prev, bonafide: errorMessage }));
      setBonafideCertificate(null);
    } finally {
      setUploadingBonafide(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!photoUrl) return;

    try {
      await deleteImage(photoUrl);
      setPhotoUrl(null);
      setPhoto(null);
      toast.success("Photo removed successfully");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete photo";
      toast.error(errorMessage);
    }
  };

  const handleDeleteBonafide = async () => {
    if (!bonafideUrl) return;

    try {
      await deleteImage(bonafideUrl);
      setBonafideUrl(null);
      setBonafideCertificate(null);
      toast.success("Bonafide certificate removed successfully");
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to delete bonafide certificate";
      toast.error(errorMessage);
    }
  };

  const handleAddInstitution = () => {
    setInstitutions((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        name: "",
        location: "",
        degree: "",
        fieldOfStudy: "",
        yearStart: "",
        targetedYearCompletion: "",
      },
    ]);
  };

  const handleDeleteInstitution = (id) => {
    setInstitutions((prev) => prev.filter((inst) => inst.id !== id));
    setErrors((prev) => {
      const newInstitutionErrors = { ...prev.institutions };
      delete newInstitutionErrors[id];
      return { ...prev, institutions: newInstitutionErrors };
    });
  };

  const handleInstitutionChange = (id, field, value) => {
    setInstitutions((prev) =>
      prev.map((inst) =>
        inst.id === id
          ? {
              ...inst,
              [field]: value,
            }
          : inst
      )
    );
    // Always validate on change
    validateInstitutionField(id, field, value);
  };

  const handleSendEmailOtp = async () => {
    if (!validateField("email", email)) {
      return;
    }

    try {
      await sendEmailOtp({ email });
      setEmailOtpSent(true);
      toast.success("Email OTP sent successfully.");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to send Email OTP.";
      toast.error(errorMessage);
    }
  };

  const handleVerifyEmailOtp = async () => {
    if (!emailOtp) {
      toast.error("Please enter OTP");
      return;
    }

    try {
      await verifyEmailOtp({ email, otp: emailOtp });
      setIsEmailVerified(true);
      toast.success("Email verified successfully.");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Email verification failed.";
      toast.error(errorMessage);
    }
  };

  const handleSendMobileOtp = async () => {
    if (!validateField("mobile", mobile)) {
      return;
    }

    try {
      await sendMobileOtp({ mobile });
      setMobileOtpSent(true);
      toast.success("Mobile OTP sent successfully.");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to send Mobile OTP.";
      toast.error(errorMessage);
    }
  };

  const handleVerifyMobileOtp = async () => {
    if (!mobileOtp) {
      toast.error("Please enter OTP");
      return;
    }

    try {
      await verifyMobileOtp({ mobile, otp: mobileOtp });
      setIsMobileVerified(true);
      toast.success("Mobile verified successfully.");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Mobile verification failed.";
      toast.error(errorMessage);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Create new errors object to collect all validation errors
    const newErrors = {
      fullName: "",
      dateOfBirth: "",
      email: "",
      mobile: "",
      address: "",
      photo: "",
      bonafide: "",
      coursesCompleted: "",
      agreeTerms: "",
      institutions: {},
    };

    let isValid = true;

    // Validate all basic fields
    if (!fullName || !fullName.trim()) {
      newErrors.fullName = "Full Name is required";
      isValid = false;
    }

    if (!dateOfBirth) {
      newErrors.dateOfBirth = "Date of Birth is required";
      isValid = false;
    }

    if (!email || !email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    } else if (!isEmailVerified) {
      newErrors.email = "Please verify your email before submitting";
      isValid = false;
    }

    if (!mobile || !mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
      isValid = false;
    } else if (mobile.length !== 10) {
      newErrors.mobile = "Mobile number must be exactly 10 digits";
      isValid = false;
    } else if (!isMobileVerified) {
      newErrors.mobile = "Please verify your mobile before submitting";
      isValid = false;
    }

    if (!address || !address.trim()) {
      newErrors.address = "Address is required";
      isValid = false;
    }

    if (!photoUrl) {
      newErrors.photo = "Photo is required";
      isValid = false;
    }

    if (!bonafideUrl) {
      newErrors.bonafide = "Bonafide certificate is required";
      isValid = false;
    }

    if (!coursesCompleted) {
      newErrors.coursesCompleted = "Please select an option";
      isValid = false;
    }

    if (!agreeTerms) {
      newErrors.agreeTerms = "You must agree to the terms and conditions";
      isValid = false;
    }

    // Validate institutions
    for (const inst of institutions) {
      const instErrors = {};

      if (!inst.name || !inst.name.trim()) {
        instErrors.name = "Name is required";
        isValid = false;
      }

      if (!inst.location || !inst.location.trim()) {
        instErrors.location = "Location is required";
        isValid = false;
      }

      if (!inst.degree || !inst.degree.trim()) {
        instErrors.degree = "Degree is required";
        isValid = false;
      }

      if (!inst.fieldOfStudy || !inst.fieldOfStudy.trim()) {
        instErrors.fieldOfStudy = "Field Of Study is required";
        isValid = false;
      }

      if (!inst.yearStart || !inst.yearStart.trim()) {
        instErrors.yearStart = "Year Start is required";
        isValid = false;
      }

      if (!inst.targetedYearCompletion || !inst.targetedYearCompletion.trim()) {
        instErrors.targetedYearCompletion =
          "Targeted Year Completion is required";
        isValid = false;
      }

      if (Object.keys(instErrors).length > 0) {
        newErrors.institutions[inst.id] = instErrors;
      }
    }

    // Update all errors at once
    setErrors(newErrors);

    setLoading(true);
    try {
      const payload = {
        fullName,
        dateOfBirth,
        email,
        mobile,
        address,
        institutionDetails: institutions.map(({ id, ...rest }) => rest),
        hasCompletedSixCourses: coursesCompleted === "Yes",
        photo: photoUrl,
        bonafideCertificate: bonafideUrl,
        isEmailVerified,
        isMobileVerified,
      };

      await registerStudent(payload);
      setIsSubmitted(true);
      toast.success(
        "Registration successful! Your application has been submitted."
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Registration failed.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.$("select").niceSelect();
  }, []);

  if (isSubmitted) {
    return (
      <main>
        <Breadcrumb title="STUDENT MEMBERSHIP APPLICATION" />
        <div className="it-student-area it-instructor-style">
          <div className="container">
            <SuccessComponent />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main>
      <Breadcrumb title="STUDENT MEMBERSHIP APPLICATION" />
      <div className="it-student-area it-instructor-style">
        <div className="container">
          <div className="row">
            <div className="col-xl-12">
              <div className="it-student-bg">
                <p>
                  <span className="text-danger">*</span>Student Membership is
                  designed for college and university students who wish to
                  engage with the SAP community early in their careers. It
                  offers access to learning resources, seminars, and mentorship
                  opportunities at a minimal cost. Students can participate in
                  events, build professional networks, and gain industry
                  exposure. This membership encourages academic and career
                  growth through active involvement. It's a valuable step toward
                  developing skills and connecting with professionals in the
                  field.
                </p>
                <div className="it-signup-text mb-20">
                  <span>
                    You have an account yet?{" "}
                    <Link to="/student-login">Sign In</Link>
                  </span>
                </div>
                <div>
                  <div className="it-student-regiform">
                    <h4 className="it-student-regiform-title">
                      SAP Student Membership Application
                    </h4>
                    <div className="it-student-regiform-wrap">
                      <div className="row">
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>
                              Enter Full Name{" "}
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              type="text"
                              value={fullName}
                              onChange={(e) => {
                                setFullName(e.target.value);
                                validateField("fullName", e.target.value);
                              }}
                              onBlur={() => validateField("fullName", fullName)}
                              style={{
                                borderColor: errors.fullName ? "red" : "",
                              }}
                            />
                            {errors.fullName && (
                              <span
                                style={{
                                  color: "red",
                                  fontSize: "12px",
                                  display: "block",
                                  marginTop: "4px",
                                }}
                              >
                                {errors.fullName}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>
                              Date of Birth{" "}
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              type="date"
                              value={dateOfBirth}
                              onChange={(e) => {
                                setDateOfBirth(e.target.value);
                                validateField("dateOfBirth", e.target.value);
                              }}
                              onBlur={() =>
                                validateField("dateOfBirth", dateOfBirth)
                              }
                              style={{
                                borderColor: errors.dateOfBirth ? "red" : "",
                              }}
                            />
                            {errors.dateOfBirth && (
                              <span
                                style={{
                                  color: "red",
                                  fontSize: "12px",
                                  display: "block",
                                  marginTop: "4px",
                                }}
                              >
                                {errors.dateOfBirth}
                              </span>
                            )}
                          </div>
                        </div>
                        <div
                          className="row"
                          style={{ gap: "32px", marginBottom: "3px" }}
                        >
                          {/* Photo Upload Section */}
                          <div
                            className=""
                            style={{
                              display: "flex",
                              justifyContent: "flex-start",
                              alignItems: "flex-start",
                            }}
                          >
                            <div
                              className=""
                              style={{
                                color: "#111",
                                borderRadius: "18px",
                                width: "375px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "14px",
                                  marginBottom: "6px",
                                  fontWeight: 500,
                                  letterSpacing: "0.02em",
                                  color: "#0E2A46",
                                }}
                              >
                                Upload Photo{" "}
                                <span style={{ color: "red" }}>*</span>
                              </span>
                              <input
                                type="file"
                                accept="image/*"
                                id="photoUpload"
                                style={{ display: "none" }}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handlePhotoUpload(file);
                                }}
                              />
                              <label
                                htmlFor="photoUpload"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "10px",
                                  background: uploadingPhoto
                                    ? "#f0f0f0"
                                    : "#fff",
                                  color: "#0E2A46",
                                  borderRadius: "8px",
                                  border: errors.photo
                                    ? "1.5px solid red"
                                    : "1.5px solid #bbb",
                                  padding: "13px 28px",
                                  width: "250px",
                                  textAlign: "center",
                                  cursor: uploadingPhoto
                                    ? "not-allowed"
                                    : "pointer",
                                  fontWeight: 500,
                                  fontSize: "0.9rem",
                                  transition: "border 0.17s, box-shadow 0.17s",
                                  boxShadow: "0 1px 3px #0001",
                                  pointerEvents: uploadingPhoto
                                    ? "none"
                                    : "auto",
                                }}
                                onMouseEnter={(e) => {
                                  if (!uploadingPhoto) {
                                    e.currentTarget.style.border = errors.photo
                                      ? "1.5px solid red"
                                      : "1.5px solid #222";
                                    e.currentTarget.style.boxShadow =
                                      "0 2px 7px #0002";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!uploadingPhoto) {
                                    e.currentTarget.style.border = errors.photo
                                      ? "1.5px solid red"
                                      : "1.5px solid #bbb";
                                    e.currentTarget.style.boxShadow =
                                      "0 1px 3px #0001";
                                  }
                                }}
                              >
                                {uploadingPhoto
                                  ? "Uploading..."
                                  : "Choose Photo"}
                              </label>
                              {errors.photo && (
                                <span
                                  style={{
                                    color: "red",
                                    fontSize: "12px",
                                    display: "block",
                                    marginTop: "4px",
                                  }}
                                >
                                  {errors.photo}
                                </span>
                              )}
                              {photoUrl && (
                                <div
                                  style={{
                                    marginTop: "10px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "8px",
                                    width: "100%",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "12px",
                                        color: "#333",
                                      }}
                                    >
                                      Selected: {photo?.name}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={handleDeletePhoto}
                                      style={{
                                        padding: "4px 8px",
                                        backgroundColor: "#dc3545",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        fontSize: "11px",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  <img
                                    src={photoUrl}
                                    alt="Uploaded photo preview"
                                    style={{
                                      maxWidth: "100px",
                                      maxHeight: "100px",
                                      objectFit: "cover",
                                      borderRadius: "6px",
                                      border: "1px solid #ddd",
                                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Bonafide Certificate Upload Section */}
                          <div
                            className=""
                            style={{
                              display: "flex",
                              justifyContent: "flex-start",
                              alignItems: "flex-start",
                            }}
                          >
                            <div
                              className=""
                              style={{
                                color: "#0E2A46",
                                borderRadius: "18px",
                                width: "375px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "14px",
                                  marginBottom: "6px",
                                  fontWeight: 500,
                                  letterSpacing: "0.02em",
                                }}
                              >
                                Upload Bonafide Image{" "}
                                <span style={{ color: "red" }}>*</span>
                              </span>
                              <input
                                type="file"
                                accept="image/*"
                                id="bonafideUpload"
                                style={{ display: "none" }}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleBonafideUpload(file);
                                }}
                              />
                              <label
                                htmlFor="bonafideUpload"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "10px",
                                  background: uploadingBonafide
                                    ? "#f0f0f0"
                                    : "#fff",
                                  color: "#0E2A46",
                                  borderRadius: "8px",
                                  border: errors.bonafide
                                    ? "1.5px solid red"
                                    : "1.5px solid #bbb",
                                  padding: "13px 28px",
                                  width: "250px",
                                  cursor: uploadingBonafide
                                    ? "not-allowed"
                                    : "pointer",
                                  fontWeight: 500,
                                  fontSize: "0.9rem",
                                  transition: "border 0.17s, box-shadow 0.17s",
                                  boxShadow: "0 1px 3px #0001",
                                  pointerEvents: uploadingBonafide
                                    ? "none"
                                    : "auto",
                                }}
                                onMouseEnter={(e) => {
                                  if (!uploadingBonafide) {
                                    e.currentTarget.style.border =
                                      errors.bonafide
                                        ? "1.5px solid red"
                                        : "1.5px solid #222";
                                    e.currentTarget.style.boxShadow =
                                      "0 2px 7px #0002";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!uploadingBonafide) {
                                    e.currentTarget.style.border =
                                      errors.bonafide
                                        ? "1.5px solid red"
                                        : "1.5px solid #bbb";
                                    e.currentTarget.style.boxShadow =
                                      "0 1px 3px #0001";
                                  }
                                }}
                              >
                                {uploadingBonafide
                                  ? "Uploading..."
                                  : "Choose Bonafide Image"}
                              </label>
                              {errors.bonafide && (
                                <span
                                  style={{
                                    color: "red",
                                    fontSize: "12px",
                                    display: "block",
                                    marginTop: "4px",
                                  }}
                                >
                                  {errors.bonafide}
                                </span>
                              )}
                              {bonafideUrl && (
                                <div
                                  style={{
                                    marginTop: "10px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "8px",
                                    width: "100%",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "12px",
                                        color: "#333",
                                      }}
                                    >
                                      Selected: {bonafideCertificate?.name}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={handleDeleteBonafide}
                                      style={{
                                        padding: "4px 8px",
                                        backgroundColor: "#dc3545",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        fontSize: "11px",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  <img
                                    src={bonafideUrl}
                                    alt="Uploaded bonafide certificate preview"
                                    style={{
                                      maxWidth: "100px",
                                      maxHeight: "100px",
                                      objectFit: "cover",
                                      borderRadius: "6px",
                                      border: "1px solid #ddd",
                                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="it-student-regiform mt-6">
                    <h4 className="it-student-regiform-title">
                      Communication Details
                    </h4>
                    <div className="it-student-regiform-wrap">
                      <div className="row">
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>
                              Email ID <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              type="email"
                              value={email}
                              onChange={(e) => {
                                setEmail(e.target.value);
                                setIsEmailVerified(false);
                                validateField("email", e.target.value);
                              }}
                              onBlur={() => validateField("email", email)}
                              disabled={isEmailVerified}
                              style={{
                                borderColor: errors.email ? "red" : "",
                              }}
                            />
                            {errors.email && (
                              <span
                                style={{
                                  color: "red",
                                  fontSize: "12px",
                                  display: "block",
                                  marginTop: "4px",
                                }}
                              >
                                {errors.email}
                              </span>
                            )}
                            {!isEmailVerified && (
                              <button
                                type="button"
                                onClick={handleSendEmailOtp}
                                disabled={!email || !!errors.email}
                                style={{
                                  marginTop: "8px",
                                  padding: "8px 16px",
                                  backgroundColor: "#0E2A46",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "5px",
                                  cursor:
                                    email && !errors.email
                                      ? "pointer"
                                      : "not-allowed",
                                  opacity: email && !errors.email ? 1 : 0.6,
                                }}
                              >
                                {emailOtpSent ? "Resend OTP" : "Send OTP"}
                              </button>
                            )}
                            {emailOtpSent && !isEmailVerified && (
                              <div style={{ marginTop: "8px" }}>
                                <input
                                  type="text"
                                  placeholder="Enter Email OTP"
                                  value={emailOtp}
                                  onChange={(e) => setEmailOtp(e.target.value)}
                                  style={{
                                    padding: "8px",
                                    marginRight: "8px",
                                    borderRadius: "5px",
                                    border: "1px solid #ccc",
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={handleVerifyEmailOtp}
                                  className="mt-6"
                                  disabled={!emailOtp}
                                  style={{
                                    padding: "8px 16px",
                                    backgroundColor: "#10b981",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "5px",
                                    cursor: emailOtp
                                      ? "pointer"
                                      : "not-allowed",
                                    opacity: emailOtp ? 1 : 0.6,
                                  }}
                                >
                                  Verify OTP
                                </button>
                              </div>
                            )}
                            {isEmailVerified && (
                              <span
                                style={{
                                  color: "green",
                                  marginTop: "8px",
                                  display: "block",
                                }}
                              >
                                ✔️ Email Verified
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>
                              Mobile (10 digits){" "}
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              type="text"
                              value={mobile}
                              onChange={(e) =>
                                handleMobileChange(e.target.value)
                              }
                              onBlur={() => validateField("mobile", mobile)}
                              disabled={isMobileVerified}
                              maxLength={10}
                              placeholder="Enter 10 digit mobile number"
                              style={{
                                borderColor: errors.mobile ? "red" : "",
                              }}
                            />
                            {errors.mobile && (
                              <span
                                style={{
                                  color: "red",
                                  fontSize: "12px",
                                  display: "block",
                                  marginTop: "4px",
                                }}
                              >
                                {errors.mobile}
                              </span>
                            )}
                            {!isMobileVerified && (
                              <button
                                type="button"
                                onClick={handleSendMobileOtp}
                                disabled={
                                  !mobile ||
                                  mobile.length !== 10 ||
                                  !!errors.mobile
                                }
                                style={{
                                  marginTop: "8px",
                                  padding: "8px 16px",
                                  backgroundColor: "#0E2A46",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "5px",
                                  cursor:
                                    mobile &&
                                    mobile.length === 10 &&
                                    !errors.mobile
                                      ? "pointer"
                                      : "not-allowed",
                                  opacity:
                                    mobile &&
                                    mobile.length === 10 &&
                                    !errors.mobile
                                      ? 1
                                      : 0.6,
                                }}
                              >
                                {mobileOtpSent ? "Resend OTP" : "Send OTP"}
                              </button>
                            )}
                            {mobileOtpSent && !isMobileVerified && (
                              <div style={{ marginTop: "8px" }}>
                                <input
                                  type="text"
                                  placeholder="Enter Mobile OTP"
                                  value={mobileOtp}
                                  onChange={(e) => setMobileOtp(e.target.value)}
                                  style={{
                                    padding: "8px",
                                    marginRight: "8px",
                                    borderRadius: "5px",
                                    border: "1px solid #ccc",
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={handleVerifyMobileOtp}
                                  disabled={!mobileOtp}
                                  className="mt-6"
                                  style={{
                                    padding: "8px 16px",
                                    backgroundColor: "#10b981",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "5px",
                                    cursor: mobileOtp
                                      ? "pointer"
                                      : "not-allowed",
                                    opacity: mobileOtp ? 1 : 0.6,
                                  }}
                                >
                                  Verify OTP
                                </button>
                              </div>
                            )}
                            {isMobileVerified && (
                              <span
                                style={{
                                  color: "green",
                                  marginTop: "8px",
                                  display: "block",
                                }}
                              >
                                ✔️ Mobile Verified
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-12">
                          <div className="it-student-regiform-item">
                            <label>
                              Address <span style={{ color: "red" }}>*</span>
                            </label>
                            <textarea
                              value={address}
                              onChange={(e) => {
                                setAddress(e.target.value);
                                validateField("address", e.target.value);
                              }}
                              onBlur={() => validateField("address", address)}
                              style={{
                                border: errors.address
                                  ? "1px solid red"
                                  : "1px solid black",
                                borderRadius: "10px",
                                height: "180px",
                              }}
                            ></textarea>
                            {errors.address && (
                              <span
                                style={{
                                  color: "red",
                                  fontSize: "12px",
                                  display: "block",
                                  marginTop: "4px",
                                }}
                              >
                                {errors.address}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="it-student-regiform">
                    <h4 className="it-student-regiform-title">
                      Institutional Details
                    </h4>
                    <div className="it-student-regiform-wrap">
                      {institutions.map((inst, index) => (
                        <div
                          key={inst.id}
                          className="institution-row"
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            borderBottom: "1px solid #eee",
                            marginBottom: "24px",
                            paddingBottom: "10px",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div className="row">
                              <div className="col-xl-8">
                                <div className="it-student-regiform-item">
                                  <label>
                                    Name of Present Institution{" "}
                                    <span style={{ color: "red" }}>*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={inst.name}
                                    onChange={(e) =>
                                      handleInstitutionChange(
                                        inst.id,
                                        "name",
                                        e.target.value
                                      )
                                    }
                                    onBlur={() =>
                                      validateInstitutionField(
                                        inst.id,
                                        "name",
                                        inst.name
                                      )
                                    }
                                    style={{
                                      borderColor: errors.institutions?.[
                                        inst.id
                                      ]?.name
                                        ? "red"
                                        : "",
                                    }}
                                  />
                                  {errors.institutions?.[inst.id]?.name && (
                                    <span
                                      style={{
                                        color: "red",
                                        fontSize: "12px",
                                        display: "block",
                                        marginTop: "4px",
                                      }}
                                    >
                                      {errors.institutions[inst.id].name}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="col-xl-4">
                                <div className="it-student-regiform-item">
                                  <label>
                                    Location{" "}
                                    <span style={{ color: "red" }}>*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={inst.location}
                                    onChange={(e) =>
                                      handleInstitutionChange(
                                        inst.id,
                                        "location",
                                        e.target.value
                                      )
                                    }
                                    onBlur={() =>
                                      validateInstitutionField(
                                        inst.id,
                                        "location",
                                        inst.location
                                      )
                                    }
                                    style={{
                                      borderColor: errors.institutions?.[
                                        inst.id
                                      ]?.location
                                        ? "red"
                                        : "",
                                    }}
                                  />
                                  {errors.institutions?.[inst.id]?.location && (
                                    <span
                                      style={{
                                        color: "red",
                                        fontSize: "12px",
                                        display: "block",
                                        marginTop: "4px",
                                      }}
                                    >
                                      {errors.institutions[inst.id].location}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="col-xl-6">
                                <div className="it-student-regiform-item">
                                  <label>
                                    Degree{" "}
                                    <span style={{ color: "red" }}>*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={inst.degree}
                                    onChange={(e) =>
                                      handleInstitutionChange(
                                        inst.id,
                                        "degree",
                                        e.target.value
                                      )
                                    }
                                    onBlur={() =>
                                      validateInstitutionField(
                                        inst.id,
                                        "degree",
                                        inst.degree
                                      )
                                    }
                                    style={{
                                      borderColor: errors.institutions?.[
                                        inst.id
                                      ]?.degree
                                        ? "red"
                                        : "",
                                    }}
                                  />
                                  {errors.institutions?.[inst.id]?.degree && (
                                    <span
                                      style={{
                                        color: "red",
                                        fontSize: "12px",
                                        display: "block",
                                        marginTop: "4px",
                                      }}
                                    >
                                      {errors.institutions[inst.id].degree}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="col-xl-6">
                                <div className="it-student-regiform-item">
                                  <label>
                                    Field of Study{" "}
                                    <span style={{ color: "red" }}>*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={inst.fieldOfStudy}
                                    onChange={(e) =>
                                      handleInstitutionChange(
                                        inst.id,
                                        "fieldOfStudy",
                                        e.target.value
                                      )
                                    }
                                    onBlur={() =>
                                      validateInstitutionField(
                                        inst.id,
                                        "fieldOfStudy",
                                        inst.fieldOfStudy
                                      )
                                    }
                                    style={{
                                      borderColor: errors.institutions?.[
                                        inst.id
                                      ]?.fieldOfStudy
                                        ? "red"
                                        : "",
                                    }}
                                  />
                                  {errors.institutions?.[inst.id]
                                    ?.fieldOfStudy && (
                                    <span
                                      style={{
                                        color: "red",
                                        fontSize: "12px",
                                        display: "block",
                                        marginTop: "4px",
                                      }}
                                    >
                                      {
                                        errors.institutions[inst.id]
                                          .fieldOfStudy
                                      }
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="col-xl-6">
                                <div className="it-student-regiform-item">
                                  <label>
                                    Year of Start{" "}
                                    <span style={{ color: "red" }}>*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={inst.yearStart}
                                    onChange={(e) =>
                                      handleInstitutionChange(
                                        inst.id,
                                        "yearStart",
                                        e.target.value
                                      )
                                    }
                                    onBlur={() =>
                                      validateInstitutionField(
                                        inst.id,
                                        "yearStart",
                                        inst.yearStart
                                      )
                                    }
                                    style={{
                                      borderColor: errors.institutions?.[
                                        inst.id
                                      ]?.yearStart
                                        ? "red"
                                        : "",
                                    }}
                                  />
                                  {errors.institutions?.[inst.id]
                                    ?.yearStart && (
                                    <span
                                      style={{
                                        color: "red",
                                        fontSize: "12px",
                                        display: "block",
                                        marginTop: "4px",
                                      }}
                                    >
                                      {errors.institutions[inst.id].yearStart}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="col-xl-6">
                                <div className="it-student-regiform-item">
                                  <label>
                                    Targeted Year of Completion{" "}
                                    <span style={{ color: "red" }}>*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={inst.targetedYearCompletion}
                                    onChange={(e) =>
                                      handleInstitutionChange(
                                        inst.id,
                                        "targetedYearCompletion",
                                        e.target.value
                                      )
                                    }
                                    onBlur={() =>
                                      validateInstitutionField(
                                        inst.id,
                                        "targetedYearCompletion",
                                        inst.targetedYearCompletion
                                      )
                                    }
                                    style={{
                                      borderColor: errors.institutions?.[
                                        inst.id
                                      ]?.targetedYearCompletion
                                        ? "red"
                                        : "",
                                    }}
                                  />
                                  {errors.institutions?.[inst.id]
                                    ?.targetedYearCompletion && (
                                    <span
                                      style={{
                                        color: "red",
                                        fontSize: "12px",
                                        display: "block",
                                        marginTop: "4px",
                                      }}
                                    >
                                      {
                                        errors.institutions[inst.id]
                                          .targetedYearCompletion
                                      }
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: "8px",
                              minWidth: "90px",
                              marginTop: "18px",
                            }}
                          >
                            {institutions.length > 1 && (
                              <button
                                type="button"
                                aria-label="Delete Institution"
                                onClick={() => handleDeleteInstitution(inst.id)}
                                style={{
                                  backgroundColor: "#f8d7da",
                                  border: "none",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  padding: "8px",
                                  color: "#dc3545",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "background-color 0.2s",
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    "#f5c2c7")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    "#f8d7da")
                                }
                              >
                                <Trash size={20} />
                              </button>
                            )}

                            <button
                              type="button"
                              aria-label="Add Institution"
                              onClick={handleAddInstitution}
                              style={{
                                backgroundColor: "#d1fae5",
                                border: "none",
                                borderRadius: "8px",
                                cursor: "pointer",
                                padding: "8px",
                                color: "#10b981",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "background-color 0.2s",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  "#a7f3d0")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  "#d1fae5")
                              }
                            >
                              <Plus size={20} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="it-student-regiform">
                    <h6>
                      Past, relevant studies (List all degree programs in
                      aquaculture pursued prior to present program providing{" "}
                      <br className="d-none d-md-block" /> name of the
                      institution, degree, major, number of years of study)
                    </h6>
                    <p>
                      Have you completed at least six courses in aquaculture or
                      a related discipline?{" "}
                      <span style={{ color: "red" }}>*</span>
                    </p>
                    <div className="it-student-regiform-wrap">
                      <div className="row">
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <div
                              className="form-radio"
                              style={{ display: "flex", alignItems: "start" }}
                            >
                              <input
                                className="radio-btn"
                                type="radio"
                                id="yes"
                                name="coursesCompleted"
                                value="Yes"
                                checked={coursesCompleted === "Yes"}
                                onChange={(e) => {
                                  setCoursesCompleted(e.target.value);
                                  validateField(
                                    "coursesCompleted",
                                    e.target.value
                                  );
                                }}
                              />
                              <label htmlFor="yes">Yes</label>
                            </div>
                            <div
                              className="form-radio"
                              style={{ display: "flex", alignItems: "start" }}
                            >
                              <input
                                className="radio-btn"
                                type="radio"
                                id="no"
                                name="coursesCompleted"
                                value="No"
                                checked={coursesCompleted === "No"}
                                onChange={(e) => {
                                  setCoursesCompleted(e.target.value);
                                  validateField(
                                    "coursesCompleted",
                                    e.target.value
                                  );
                                }}
                              />
                              <label htmlFor="no">No</label>
                            </div>
                            {errors.coursesCompleted && (
                              <span
                                style={{
                                  color: "red",
                                  fontSize: "12px",
                                  display: "block",
                                  marginTop: "4px",
                                }}
                              >
                                {errors.coursesCompleted}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="col-xl-12">
                        <div className="it-instructor-wrap">
                          <div className="it-signup-agree">
                            <div
                              className="form-check "
                              style={{
                                display: "flex",
                                alignItems: "start",
                              }}
                            >
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={agreeTerms}
                                onChange={(e) => {
                                  setAgreeTerms(e.target.checked);
                                  validateField("agreeTerms", e.target.checked);
                                }}
                                id="flexCheckDefault"
                              />
                              <label
                                className="form-check-label"
                                htmlFor="flexCheckDefault"
                              >
                                I would like to apply for Student Membership at
                                the Society of Aquaculture Professionals and I
                                state that I am aware of the rules and
                                regulations of becoming a member and the
                                benefits of the membership.{" "}
                                <span style={{ color: "red" }}>*</span>
                              </label>
                            </div>
                            {errors.agreeTerms && (
                              <span
                                style={{
                                  color: "red",
                                  fontSize: "12px",
                                  display: "block",
                                  marginTop: "4px",
                                }}
                              >
                                {errors.agreeTerms}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="it-student-regiform-btn">
                        <button
                          onClick={handleSubmit}
                          className="ed-btn-theme"
                          type="button"
                        >
                          <span>
                            {loading ? "Submitting..." : "Register Now"}
                            <i>
                              <RightArrow />
                            </i>
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default StudentRegisterMain;
